import { Fragment, MouseEvent, useEffect } from 'react';
import styled from 'styled-components';
import toNumber from 'lodash/toNumber';
import isElement from 'lodash/isElement';
import { doesElementExceedWindowBottom, setElementHeightBasedOnWindowEnd } from '../../../shared/utils/utils-clipping';
import { Trans, useTranslation } from 'react-i18next';

const HintToggle = styled.a<{ readonly marginTop?: string; readonly marginLeft?: string }>`
    display: flex;
    align-items: center;
    margin-top: ${props => (props.marginTop ? props.marginTop : '0')};
    margin-left: ${props => (props.marginLeft ? props.marginLeft : '0')};
    font-size: 14px;
    color: black;
`;

const InfoIcon = styled.i<{ readonly lineHeight?: string }>`
    color: #7fc31c;
    line-height: ${props => (props.lineHeight ? props.lineHeight : '1')} !important;
`;

const BoldDiv = styled.div`
    font-weight: bold;
`;

const HorizontalRule = styled.hr<{ readonly borderOverflow: number }>`
    margin: 15px -${props => props.borderOverflow}px;
    border-top: 1px solid #afafae;
`;

const HintContainer = styled.div<{ readonly marginLeftRight: number }>`
    font-size: 14px;
    margin: 0 ${props => props.marginLeftRight}px;
`;

const StyledExpandIcon = styled.i<{ readonly lineHeight?: string }>`
    line-height: ${props => (props.lineHeight ? props.lineHeight : '1')} !important;
`;

type Props = {
    readonly isHintShown: boolean;
    readonly borderOverflow?: string;
    readonly marginLeftRight?: string;
    readonly marginTop?: string;
    readonly marginLeft?: string;
    readonly lineHeight?: string;
    readonly toggleHint: (event: MouseEvent<HTMLAnchorElement>) => void;
};

export const ReserveHint = (props: Props) => {
    const { t } = useTranslation();
    useEffect(() => {
        if (props.isHintShown) {
            const modalWrapperToResize = document.getElementsByClassName('js-reserve-settings-height-resize')?.[0];
            if (!isElement(modalWrapperToResize)) {
                return;
            }

            const modalWillOverflowWindow = doesElementExceedWindowBottom(modalWrapperToResize);
            if (!modalWillOverflowWindow) {
                return;
            }

            const newHeight = setElementHeightBasedOnWindowEnd(modalWrapperToResize, 1);
            (modalWrapperToResize as HTMLElement).style.height = `${newHeight}px`;
        }
    }, [props.isHintShown]);

    const borderOverflow = props.borderOverflow?.includes('px') ? toNumber(props.borderOverflow.slice(0, -2)) : 0;
    const marginLeftRight = props.marginLeftRight?.includes('px') ? toNumber(props.marginLeftRight.slice(0, -2)) : 0;

    return (
        <>
            <HintToggle marginTop={props.marginTop} marginLeft={props.marginLeft} onClick={props.toggleHint}>
                <InfoIcon className="material-icons md-up" lineHeight={props.lineHeight}>
                    info
                </InfoIcon>
                &nbsp;
                <span>{t('reserveReports.help.help')}</span>
                <StyledExpandIcon id="reserve-hint-arrow" className="material-icons md-up">
                    {props.isHintShown ? 'expand_less' : 'expand_more'}
                </StyledExpandIcon>
            </HintToggle>
            {props.isHintShown ? (
                <HintContainer marginLeftRight={marginLeftRight}>
                    <HorizontalRule borderOverflow={borderOverflow} />
                    <BoldDiv>{t('reserveReports.help.reservesPerCopyLimitTitle')}</BoldDiv>
                    <p>{t('reserveReports.help.reservesPerCopyLimit')}</p>
                    <BoldDiv>{t('reserveReports.help.maxCopiesInCollectionTitle')}</BoldDiv>
                    <p>{t('reserveReports.help.maxCopiesInCollection')}</p>
                    <BoldDiv>{t('reserveReports.help.calculationOfAdditionalCopiesTitle')}</BoldDiv>
                    <p>
                        <Trans i18nKey="reserveReports.help.calculationOfAdditionalCopies" components={[<span />]} />
                    </p>
                    <p>
                        <Trans i18nKey="reserveReports.help.standard" components={[<span />]} />
                    </p>
                    <p>
                        <Trans i18nKey="reserveReports.help.proactive" components={[<span />]} />
                    </p>
                    <BoldDiv>{t('reserveReports.generateReserveReport')}</BoldDiv>
                    <p>{t('reserveReports.help.generateReserveReport')}</p>
                    <BoldDiv>{t('reserveReports.help.emailRecipientsTitle')}</BoldDiv>
                    <p>{t('reserveReports.help.emailRecipients')}</p>
                    <BoldDiv>{t('reserveReports.help.separateReportsPerFormatTitle')}</BoldDiv>
                    <p>{t('reserveReports.help.separateReportsPerFormat')}</p>
                    <BoldDiv>{t('reserveReports.help.automaticTopUpsTitle')}</BoldDiv>
                    <p>{t('reserveReports.help.automaticTopUps')}</p>
                    <BoldDiv>{t('reserveReports.defaultCustomSettings')}</BoldDiv>
                    <p>{t('reserveReports.help.defaultCustomSettings')}</p>
                </HintContainer>
            ) : null}
        </>
    );
};
